/**
 * THE single rule for what commercial surfaces Nuvra may render, given where it is running.
 *
 * Google Play's Payments policy is blunt: an app distributed through Play that sells digital
 * content used inside that app must sell it through Google Play Billing. An external checkout
 * link, a Flutterwave or Paystack redirect, a "Pay with card" button, or any hosted provider page
 * reached from inside the Android build is grounds for removal, and removal takes the listing with
 * it. So the Android surface must not merely discourage web checkout; it must be structurally
 * incapable of rendering it.
 *
 * The failure mode this file is built to prevent is not "we forgot to hide the pricing page". It
 * is the twentieth payment call to action, the one buried in a rewards toast or a watermark
 * setting, that nobody remembers when they hide the obvious ones. A repository search found
 * payment surfaces across roughly thirty files. Hiding thirty files by hand is not a policy, it is
 * thirty chances to be wrong, and the thirty-first component added next month will be wrong by
 * default. So the rule lives here once, every surface asks it, and the audit test asserts that no
 * component reaches a payment provider without asking.
 *
 * Equally important, and the reason this is a policy rather than a kill switch: the WEB product
 * must behave EXACTLY as it did before. Web is the default answer, unchanged, with every surface
 * allowed. Nothing here narrows the web.
 *
 * This layer decides only what may be SHOWN and by which mechanism. It grants nothing. Entitlement
 * remains where it already is, on the server, in subscriptionService.applyActivation, whichever
 * surface the purchase came from. Two purchase surfaces, one authority.
 */

import { currentPlatform, type AppPlatform } from "./platform";

/**
 * How money can move on this surface.
 *
 * "web_checkout" is hosted provider pages (the existing Flutterwave / Paystack / Stripe / Paddle /
 * Lemon Squeezy path). "google_play" and "app_store" are the store billing systems, each the only
 * permitted purchase mechanism inside its own build.
 *
 * Three values rather than a boolean, because the store the purchase went through decides which
 * server route verifies it. Callers that only need to know "not the web" ask `storeBillingRequired`
 * instead, so adding a fourth surface later does not touch every call site.
 */
export type BillingMode = "web_checkout" | "google_play" | "app_store";

const STORE_MODE: Record<Exclude<AppPlatform, "web">, BillingMode> = {
  android: "google_play",
  ios: "app_store",
};

/**
 * Every distinct commercial action in the product. A surface asks about ITSELF, not about the
 * platform, which is what keeps platform knowledge out of thirty components.
 */
export type PaymentSurface =
  /** Subscribe, upgrade, or change plan. */
  | "plan_upgrade"
  /** Buy ACU credits (a consumable, not a subscription). */
  | "acu_topup"
  /** Buy another creator's marketplace artifact. */
  | "marketplace_purchase"
  /** Seller-side money OUT: bank details, Stripe Connect onboarding, withdrawals. */
  | "payout_management"
  /**
   * Any redirect to a hosted payment page or external billing URL. This is the surface that is
   * categorically forbidden on Android, and it is separated from the others precisely so a future
   * Play-Billing purchase flow can be allowed without dragging external checkout back in with it.
   */
  | "external_checkout"
  /** Showing a price or plan comparison with no purchase action attached. */
  | "price_display";

const ALL_SURFACES: readonly PaymentSurface[] = [
  "plan_upgrade",
  "acu_topup",
  "marketplace_purchase",
  "payout_management",
  "external_checkout",
  "price_display",
];

export interface BillingPolicy {
  readonly platform: AppPlatform;
  readonly mode: BillingMode;
  /** True only where hosted provider checkout is permitted. */
  readonly webCheckoutAllowed: boolean;
  /** True where a purchase must go through the platform's store billing rather than the web. */
  readonly storeBillingRequired: boolean;
  /** Whether a given commercial surface may render here. */
  allows(surface: PaymentSurface): boolean;
  /** Short, customer-safe explanation for a surface that is not allowed. Empty when allowed. */
  reasonFor(surface: PaymentSurface): string;
}

/**
 * Explicit deny list per native platform. Written as a deny list of what is FORBIDDEN rather than
 * an allow list of what is permitted, for one reason: a surface added later must be considered,
 * not silently permitted. `allows()` below enforces that by refusing anything it does not
 * recognise, so a new PaymentSurface value fails closed on native until somebody decides.
 */
const NATIVE_FORBIDDEN: Record<Exclude<AppPlatform, "web">, ReadonlySet<PaymentSurface>> = {
  android: new Set<PaymentSurface>([
    // Categorical. This is the Play Payments policy line.
    "external_checkout",
    // Marketplace artifacts are digital content consumed inside the app, so Play Billing would be
    // required to sell them here. Play Billing covers our own plans and credits today, not
    // arbitrary creator listings with per-creator payouts, so the honest position is that the
    // Android build browses the marketplace and does not sell in it. Not "coming soon" wording in
    // place of a feature: the buy action is absent, and the web can complete the purchase.
    "marketplace_purchase",
    // Seller payouts are money going OUT, so Play Billing does not apply and does not offer a
    // mechanism. The onboarding flow is a hosted provider journey (bank verification, Stripe
    // Connect), which is exactly the external billing journey Play objects to seeing in-app.
    "payout_management",
  ]),
  ios: new Set<PaymentSurface>([
    // The App Store's equivalent of the Play Payments rule, and enforced at least as strictly:
    // Apple's review reads an external purchase link as steering, which is a rejection.
    "external_checkout",
    // Same reasoning as Android. In-app purchase covers our own plans and credits, not arbitrary
    // creator listings with per-creator payouts, so the iOS build browses the marketplace and does
    // not sell in it. Free artifacts still install.
    "marketplace_purchase",
    // Money going OUT, so in-app purchase does not apply and offers no mechanism. The onboarding
    // journey is hosted provider pages, which is the external billing journey review objects to.
    "payout_management",
  ]),
};

/**
 * Customer-facing wording. The external-checkout line names the store, because "purchases happen
 * elsewhere" is confusing while "purchases go through the App Store" is simply how buying works
 * here. Nothing in this file's wording describes a restriction, since from the customer's side
 * nothing is broken.
 */
function reasons(mode: BillingMode): Partial<Record<PaymentSurface, string>> {
  const store = mode === "app_store" ? "the App Store" : "Google Play";
  return {
    external_checkout:    `Purchases in the app go through ${store}.`,
    marketplace_purchase: "Marketplace purchases are available on the web.",
    payout_management:    "Payout settings are managed on the web.",
  };
}

function buildPolicy(platform: AppPlatform): BillingPolicy {
  const isWebSurface = platform === "web";
  const forbidden = isWebSurface ? null : NATIVE_FORBIDDEN[platform];
  const mode: BillingMode = isWebSurface ? "web_checkout" : STORE_MODE[platform];
  const wording = reasons(mode);

  return {
    platform,
    mode,
    // Positive identification required. An unrecognised surface never gets web checkout, which is
    // the half of the fail-safe that platform detection deliberately leaves to this layer.
    webCheckoutAllowed: isWebSurface,
    storeBillingRequired: !isWebSurface,

    allows(surface: PaymentSurface): boolean {
      if (isWebSurface) return true; // The web is unchanged. Everything it could show, it shows.
      // Fail closed on native: a surface this policy has never heard of is not rendered, so
      // adding a PaymentSurface value cannot accidentally open a payment path in a store build.
      if (!ALL_SURFACES.includes(surface)) return false;
      return !forbidden!.has(surface);
    },

    reasonFor(surface: PaymentSurface): string {
      if (this.allows(surface)) return "";
      return wording[surface] ?? "This is available on the web.";
    },
  };
}

/** Pure, for tests and for anything that needs to reason about a platform other than its own. */
export function billingPolicyFor(platform: AppPlatform): BillingPolicy {
  return buildPolicy(platform);
}

/** The policy in force right now. Resolved per call; platform detection underneath is cached. */
export function currentBillingPolicy(): BillingPolicy {
  return buildPolicy(currentPlatform());
}

/** Convenience for the common question. */
export function paymentSurfaceAllowed(surface: PaymentSurface): boolean {
  return currentBillingPolicy().allows(surface);
}

export { ALL_SURFACES as PAYMENT_SURFACES };
